'use client';

import {
  Badge,
  Box,
  CloseButton,
  Collapsible,
  Drawer,
  HStack,
  Icon,
  Link as ChakraLink,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  IconArrowRight,
  IconBell,
  IconChevronDown,
  IconRefresh,
  IconSparkles,
} from '@tabler/icons-react';
import NextLink from 'next/link';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LoadingIndicator as Spinner } from '@/components/shared/LoadingIndicator';
import {
  PRODUCT_UPDATE_CATEGORY_LABELS,
  PRODUCT_UPDATES,
  type ProductUpdate,
  type ProductUpdateCategory,
} from '@/lib/product-updates';

const READ_STATE_KEY_PREFIX = 'attune:product-updates:last-seen:v1';
const UPDATE_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

const CATEGORY_TONES: Record<ProductUpdateCategory, { bg: string; border: string; color: string }> =
  {
    feature: {
      bg: 'status.info.bg',
      border: 'status.info.border',
      color: 'status.info.fg',
    },
    improvement: {
      bg: 'status.success.bg',
      border: 'status.success.border',
      color: 'status.success.fg',
    },
    fix: {
      bg: 'status.warning.bg',
      border: 'status.warning.border',
      color: 'status.warning.fg',
    },
    announcement: {
      bg: 'status.neutral.bg',
      border: 'status.neutral.border',
      color: 'status.neutral.fg',
    },
  };

function readStateKey(userId?: string | null) {
  return `${READ_STATE_KEY_PREFIX}:${userId || 'session'}`;
}

function isPublishedAfter(publishedAt: string, lastSeenAt: string | null) {
  return !lastSeenAt || Date.parse(publishedAt) > Date.parse(lastSeenAt);
}

function UpdateEntry({
  update,
  isNew,
  initiallyOpen,
  onNavigate,
}: {
  update: ProductUpdate;
  isNew: boolean;
  initiallyOpen: boolean;
  onNavigate: () => void;
}) {
  const [open, setOpen] = useState(initiallyOpen);
  const tone = CATEGORY_TONES[update.category];

  return (
    <Collapsible.Root open={open} onOpenChange={event => setOpen(event.open)}>
      <Box
        position="relative"
        pl="8"
        pr="1"
        py="1"
        _before={{
          content: '""',
          position: 'absolute',
          left: '9px',
          top: '0',
          bottom: '0',
          width: '1px',
          bg: 'ui.border.subtle',
        }}
      >
        <Box
          position="absolute"
          left="5px"
          top="23px"
          boxSize="9px"
          borderRadius="full"
          bg={isNew ? 'accent.solid' : 'ui.surface.drawer'}
          borderWidth="2px"
          borderColor={isNew ? 'accent.solid' : 'ui.border.default'}
          zIndex="1"
        />

        <Collapsible.Trigger asChild>
          <Box
            as="button"
            w="full"
            px="3.5"
            py="3"
            borderRadius="12px"
            textAlign="left"
            cursor="pointer"
            bg={open ? 'ui.surface.drawerMuted' : 'transparent'}
            _hover={{ bg: 'ui.surface.drawerMuted' }}
            _focusVisible={{ outline: 'none', boxShadow: 'none' }}
            transition="background-color 150ms ease"
          >
            <HStack justify="space-between" align="start" gap="3">
              <VStack align="start" gap="2" minW={0}>
                <HStack gap="2" flexWrap="wrap">
                  <Badge
                    bg={tone.bg}
                    borderWidth="1px"
                    borderColor={tone.border}
                    color={tone.color}
                    borderRadius="full"
                    px="2"
                    py="0.5"
                    fontSize="0.68rem"
                    fontWeight="700"
                  >
                    {PRODUCT_UPDATE_CATEGORY_LABELS[update.category]}
                  </Badge>
                  {isNew ? (
                    <Text
                      as="span"
                      fontSize="0.68rem"
                      fontWeight="750"
                      color="ui.text.accent"
                      letterSpacing="0.04em"
                      textTransform="uppercase"
                    >
                      New
                    </Text>
                  ) : null}
                  <Text as="span" fontSize="xs" color="ui.text.tertiary">
                    {UPDATE_DATE_FORMATTER.format(new Date(update.publishedAt))}
                  </Text>
                </HStack>
                <Text fontSize="sm" fontWeight="750" color="ui.text.primary" lineHeight="1.35">
                  {update.title}
                </Text>
                <Text fontSize="sm" color="ui.text.secondary" lineHeight="1.55">
                  {update.summary}
                </Text>
              </VStack>
              <Icon
                boxSize="4"
                color="ui.text.tertiary"
                flexShrink={0}
                mt="1"
                transform={open ? 'rotate(180deg)' : undefined}
                transition="transform 150ms ease"
                aria-hidden="true"
              >
                <IconChevronDown size={16} stroke={1.8} />
              </Icon>
            </HStack>
          </Box>
        </Collapsible.Trigger>

        <Collapsible.Content>
          <VStack align="stretch" gap="3" px="3.5" pt="1" pb="4">
            <Box as="ul" pl="4.5" color="ui.text.secondary">
              {update.details.map(detail => (
                <Text
                  as="li"
                  key={detail}
                  fontSize="sm"
                  lineHeight="1.55"
                  mb="1.5"
                  _marker={{ color: 'ui.text.tertiary' }}
                >
                  {detail}
                </Text>
              ))}
            </Box>
            {update.href && update.ctaLabel ? (
              <ChakraLink
                as={NextLink}
                href={update.href}
                target={update.href.startsWith('https://') ? '_blank' : undefined}
                rel={update.href.startsWith('https://') ? 'noreferrer' : undefined}
                onClick={onNavigate}
                display="inline-flex"
                alignItems="center"
                gap="1.5"
                alignSelf="flex-start"
                color="ui.text.accent"
                fontSize="sm"
                fontWeight="700"
                textDecoration="none"
                _hover={{ textDecoration: 'underline' }}
              >
                {update.ctaLabel}
                <Icon boxSize="3.5" aria-hidden="true">
                  <IconArrowRight size={14} stroke={1.8} />
                </Icon>
              </ChakraLink>
            ) : null}
          </VStack>
        </Collapsible.Content>
      </Box>
    </Collapsible.Root>
  );
}

export function ProductUpdatesDrawer({
  userId,
  isAdmin = false,
}: {
  userId?: string | null;
  isAdmin?: boolean;
}) {
  const [updates, setUpdates] = useState<ProductUpdate[]>([...PRODUCT_UPDATES]);
  const [open, setOpen] = useState(false);
  const [lastSeenAt, setLastSeenAt] = useState<string | null>(null);
  const [unreadBoundary, setUnreadBoundary] = useState<string | null>(null);
  const [readStateLoaded, setReadStateLoaded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [feedWarning, setFeedWarning] = useState<string | null>(null);

  const latestPublishedAt = updates[0]?.publishedAt ?? null;

  const refreshFeed = useCallback(async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      const response = await fetch('/api/attune/product-updates', {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Product update feed returned ${response.status}.`);
      const payload = (await response.json()) as {
        updates?: unknown;
        warning?: unknown;
      };
      if (!Array.isArray(payload.updates)) {
        throw new Error('Product update feed returned an invalid response.');
      }
      const nextUpdates = payload.updates as ProductUpdate[];
      setUpdates(nextUpdates);
      setFeedWarning(typeof payload.warning === 'string' ? payload.warning : null);
    } catch (error) {
      console.warn('[ProductUpdatesDrawer] Live feed refresh failed:', error);
      setFeedWarning('Showing bundled updates. Live announcements couldn’t refresh.');
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    setReadStateLoaded(false);
    try {
      const stored = window.localStorage.getItem(readStateKey(userId));
      setLastSeenAt(stored && Number.isFinite(Date.parse(stored)) ? stored : null);
    } catch {
      setLastSeenAt(null);
    } finally {
      setReadStateLoaded(true);
    }
  }, [userId]);

  useEffect(() => {
    void refreshFeed();
  }, [refreshFeed]);

  const unreadCount = useMemo(
    () =>
      readStateLoaded
        ? updates.filter(update => isPublishedAfter(update.publishedAt, lastSeenAt)).length
        : 0,
    [lastSeenAt, readStateLoaded, updates]
  );
  const hasUnread = unreadCount > 0;

  const markReadThrough = (publishedAt: string) => {
    setLastSeenAt(publishedAt);
    try {
      window.localStorage.setItem(readStateKey(userId), publishedAt);
    } catch {
      // The feed remains usable when browser storage is unavailable.
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) return;

    setUnreadBoundary(lastSeenAt);
    void refreshFeed();
  };

  const handleMarkAllRead = () => {
    if (!latestPublishedAt) return;
    markReadThrough(latestPublishedAt);
    setUnreadBoundary(latestPublishedAt);
  };

  return (
    <>
      <Box
        as="button"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        h="38px"
        w="38px"
        borderRadius="12px"
        border="0"
        bg={open ? 'ui.surface.input' : 'transparent'}
        color={open ? 'ui.text.primary' : 'ui.text.secondary'}
        cursor="pointer"
        position="relative"
        aria-label={
          hasUnread
            ? `What’s new in Attune, ${unreadCount} unread update${unreadCount === 1 ? '' : 's'}`
            : 'What’s new in Attune'
        }
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => handleOpenChange(true)}
        _hover={{
          bg: 'ui.surface.input',
          color: 'ui.text.primary',
        }}
        _focus={{ outline: 'none' }}
        _focusVisible={{ boxShadow: 'none' }}
        transition="background-color 0.15s ease, color 0.15s ease"
      >
        <Icon boxSize="18px" aria-hidden="true">
          <IconBell size={18} stroke={1.75} />
        </Icon>
        {hasUnread ? (
          <Box
            position="absolute"
            top="2px"
            right="1px"
            minW="17px"
            h="17px"
            px="1"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="full"
            bg="status.danger.fg"
            color="ui.text.inverse"
            fontSize="0.58rem"
            fontWeight="800"
            lineHeight="1"
            borderWidth="1px"
            borderColor="ui.surface.panel"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Box>
        ) : null}
      </Box>

      <Drawer.Root open={open} onOpenChange={event => handleOpenChange(event.open)} placement="end">
        <Portal>
          <Drawer.Backdrop bg="ui.overlay.backdrop" backdropFilter="blur(4px)" />
          <Drawer.Positioner>
            <Drawer.Content
              w={{ base: '100vw', sm: '440px' }}
              maxW="440px"
              layerStyle="drawerSurface"
              borderLeftWidth={{ base: '0', sm: '1px' }}
              borderLeftColor="ui.border.default"
              overflow="hidden"
            >
              <Drawer.Header layerStyle="drawerHeader" px="5" py="4">
                <HStack gap="3">
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    boxSize="9"
                    borderRadius="10px"
                    bg="accent.subtle"
                    color="accent.fg"
                  >
                    <IconSparkles size={18} stroke={1.75} />
                  </Box>
                  <VStack align="start" gap="0">
                    <Drawer.Title fontSize="md" fontWeight="750" color="ui.text.primary">
                      What’s new
                    </Drawer.Title>
                    <Drawer.Description fontSize="xs" color="ui.text.secondary">
                      {isRefreshing ? 'Checking for updates…' : 'The latest from Attune'}
                    </Drawer.Description>
                  </VStack>
                  {isRefreshing ? (
                    <Spinner size="xs" color="ui.text.tertiary" ml="auto" mr="8" />
                  ) : null}
                </HStack>
                <Drawer.CloseTrigger asChild position="absolute" top="3.5" right="3.5">
                  <CloseButton size="sm" aria-label="Close product updates" />
                </Drawer.CloseTrigger>
              </Drawer.Header>

              <Drawer.Body p="0" bg="ui.surface.drawer" overflowY="auto">
                <Box
                  px="5"
                  py="4"
                  bg="accent.traceSurface"
                  borderBottomWidth="1px"
                  borderColor="ui.border.subtle"
                >
                  <HStack align="start" gap="2.5">
                    <Icon boxSize="4" color="accent.fg" mt="0.5" flexShrink={0} aria-hidden="true">
                      <IconSparkles size={16} stroke={1.75} />
                    </Icon>
                    <Text flex="1" fontSize="sm" color="ui.text.secondary" lineHeight="1.5">
                      You can also ask Attune AI “What’s new?” or “What bugs were fixed?”
                    </Text>
                    {hasUnread ? (
                      <Box
                        as="button"
                        flexShrink={0}
                        color="ui.text.accent"
                        fontSize="xs"
                        fontWeight="750"
                        cursor="pointer"
                        onClick={handleMarkAllRead}
                        _hover={{ textDecoration: 'underline' }}
                      >
                        Mark all as read
                      </Box>
                    ) : null}
                  </HStack>
                </Box>

                {feedWarning ? (
                  <HStack
                    role="status"
                    mx="4"
                    mt="3"
                    px="3.5"
                    py="3"
                    gap="3"
                    align="start"
                    borderWidth="1px"
                    borderColor="status.warning.border"
                    bg="status.warning.bg"
                    borderRadius="12px"
                  >
                    <Text flex="1" fontSize="xs" color="status.warning.fg" lineHeight="1.45">
                      {feedWarning}
                    </Text>
                    <Box
                      as="button"
                      display="inline-flex"
                      alignItems="center"
                      gap="1"
                      color="status.warning.fg"
                      fontSize="xs"
                      fontWeight="700"
                      cursor="pointer"
                      aria-disabled={isRefreshing}
                      onClick={() => {
                        if (!isRefreshing) void refreshFeed();
                      }}
                    >
                      <IconRefresh size={14} stroke={1.8} />
                      Retry
                    </Box>
                  </HStack>
                ) : null}

                <VStack align="stretch" gap="0" px="4" py="3">
                  {updates.map((update, index) => (
                    <UpdateEntry
                      key={update.id}
                      update={update}
                      isNew={open && isPublishedAfter(update.publishedAt, unreadBoundary)}
                      initiallyOpen={index === 0}
                      onNavigate={() => setOpen(false)}
                    />
                  ))}

                  {isAdmin ? (
                    <Box
                      mx="2"
                      mt="3"
                      mb="2"
                      px="4"
                      py="3.5"
                      borderRadius="12px"
                      borderWidth="1px"
                      borderColor="ui.border.subtle"
                      bg="ui.surface.drawerMuted"
                    >
                      <Text fontSize="xs" fontWeight="750" color="ui.text.primary">
                        Admin publishing
                      </Text>
                      <Text mt="1" fontSize="xs" color="ui.text.secondary" lineHeight="1.5">
                        Ask your connected Attune MCP client to prepare an announcement. You’ll
                        review the exact customer preview before it can go live.
                      </Text>
                      <Text mt="2" fontFamily="mono" fontSize="0.68rem" color="ui.text.tertiary">
                        product_updates.prepare_create
                      </Text>
                    </Box>
                  ) : null}
                </VStack>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </>
  );
}
